import React from 'react';
import { Badge, Icon, IndexTable, RadioButton, Tabs, Text } from '@shopify/polaris';
import Language from '../../../../../i18n/Language';
import { numToUnit } from '../extraFunc';
import { MagicIcon } from '@shopify/polaris-icons';

const t = Language;

// 空模块
const emptyModule = (text = "") => {
    return <div className='audit_empty_module'>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_11100_18524)">
                <rect width="60" height="60" fill="white" />
                <rect x="36.3108" y="40.373" width="3.7166" height="5.23736" transform="rotate(-47.1295 36.3108 40.373)" fill="#8c9196" />
                <rect x="38.0867" y="46.082" width="9.29151" height="2.32288" transform="rotate(-47.1295 38.0867 46.082)" fill="#8c9196" />
                <path d="M39.7893 47.6602L46.1107 40.8505L55.5547 49.6173C57.4352 51.3629 57.5445 54.3024 55.7988 56.1829C54.0532 58.0633 51.1137 58.1726 49.2333 56.427L39.7893 47.6602Z" fill="#8c9196" />
                <circle cx="23.6486" cy="25.41" r="17.3667" transform="rotate(-10.9199 23.6486 25.41)" stroke="#8c9196" strokeWidth="5.64706" />
            </g>
            <defs>
                <clipPath id="clip0_11100_18524">
                    <rect width="60" height="60" fill="white" />
                </clipPath>
            </defs>
        </svg>
        <Text as='p' variant='bodyMd' tone='subdued'>{text ? t(text) : t("No data to show here")}</Text>
    </div>
}

// 关键词建议表格（Ranking Keywords / Suggested Keywords 共用）
const KeywordTable = ({ list = [], loading = false, emptyText = "", selectedKeyword = "", onSelectKeyword, selType = 'suggested' }) => {
    const rowMarkup = (list || []).map((item, index) => {
        const checked = selectedKeyword === item.keyword;
        const comp = item.competition ? String(item.competition).toUpperCase() : '';
        return <IndexTable.Row
            id={`${item.id || item.keyword}`}
            key={item.id || item.keyword}
            position={index}
            onClick={() => onSelectKeyword(item.keyword)}
        >
            <IndexTable.Cell>
                <div className='flex_layout'>
                    <RadioButton
                        label={item.keyword}
                        labelHidden
                        checked={checked}
                        onChange={() => onSelectKeyword(item.keyword)}
                    />
                    <span className='keyword_cell'>
                        <span className='keyword_text' title={item.keyword}>{item.keyword}</span>
                    </span>
                    <span className='keyword_icon flex_layout p_magic_icon'>
                        <Icon source={MagicIcon} />
                    </span>
                </div>
            </IndexTable.Cell>
            {
                selType === 'ranking' ? (
                    <>
                        <IndexTable.Cell>
                            <span className='keyword_vol'><Text as='span' numeric>{item.clicks ? numToUnit(item.clicks) : '-'}</Text></span>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                            <span className='keyword_vol'><Text as='span' numeric>{item.impressions ? numToUnit(item.impressions) : '-'}</Text></span>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                            <span className='keyword_vol'><Text as='span' numeric>{item.position ? numToUnit(item.position) : '-'}</Text></span>
                        </IndexTable.Cell>
                    </>
                ) : (
                    <>
                        <IndexTable.Cell>
                            {
                                ["HIGH", "MEDIUM", "LOW"].includes(comp)
                                    ? <Badge tone={comp === "HIGH" ? "warning" : comp === "MEDIUM" ? "attention" : "success"}>{t(comp === "HIGH" ? "High" : comp === "MEDIUM" ? "Medium" : "Low")}</Badge>
                                    : <Text as='span' tone='subdued'>-</Text>
                            }
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                            <span className='keyword_vol'><Text as='span' numeric>{item.volume ? numToUnit(item.volume) : '-'}</Text></span>
                        </IndexTable.Cell>
                    </>
                )
            }
        </IndexTable.Row>
    });
    return <div className='keyword_suggestions_table'>
        <IndexTable
            selectable={false}
            loading={loading}
            itemCount={list ? list.length : 0}
            resourceName={{ singular: t("keyword"), plural: t("keywords") }}
            emptyState={emptyModule(emptyText)}
            headings={selType === 'ranking' ? [
                { title: t("Keyword") },
                { title: t("Clicks") },
                { title: t("Impr.") },
                { title: t("Position") },
            ] : [
                { title: t("Keyword") },
                { title: t("Competition") },
                { title: t("Vol") },
            ]}
        >
            {rowMarkup}
        </IndexTable>
    </div>
}

/**
 * 关键词建议模块（Tabs + IndexTable）
 * - Suggested Keywords: AI 建议关键词
 * - Ranking Keywords: 预留，链接 GSC 后展示页面排名关键词
 */
const KeywordSuggestions = ({
    showRanking = false,
    showSuggested = false,
    keywordTabSel = 'suggested',
    onTabChange = () => { },
    selectedKeyword = '',
    onSelectKeyword = () => { },
    suggestedList = [],
    suggestedLoading = false,
    rankingList = [],
    rankingLoading = false,
}) => {
    // 按需拼装 tabs: 仅在链接GSC且有排名数据时展示 Ranking，仅在触发AI建议后展示 Suggested
    const tabs = [];
    if (showRanking) tabs.push({ id: 'ranking', content: t("Ranking Keywords") });
    if (showSuggested) tabs.push({ id: 'suggested', content: t("Suggested Keywords") });
    if (tabs.length === 0) return null;

    // 当前选中tab，若指定的tab不可用则回退到第一个可用tab
    let selected = tabs.findIndex(tab => tab.id === keywordTabSel);
    if (selected < 0) selected = 0;
    const isRanking = tabs[selected].id === 'ranking';

    return <div className="keyword_suggestions_module">
        <Tabs
            tabs={tabs}
            selected={selected}
            onSelect={(index) => onTabChange(tabs[index].id)}
            fitted={false}
        />
        {
            isRanking
                ? <KeywordTable
                    list={rankingList}
                    loading={rankingLoading}
                    emptyText={t("No ranking keywords found")}
                    selectedKeyword={selectedKeyword}
                    onSelectKeyword={onSelectKeyword}
                    selType='ranking'
                />
                : <KeywordTable
                    list={suggestedList}
                    loading={suggestedLoading}
                    emptyText={suggestedLoading ? t("No data to show here") : t("Generation failed. Try another keyword.")}
                    selectedKeyword={selectedKeyword}
                    onSelectKeyword={onSelectKeyword}
                    selType='suggested'
                />
        }
    </div>
}

export default KeywordSuggestions;
